/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator, ValidatorFn } from '@angular/forms';
import { formatAsString } from 'app/shared/helpers/date-helpers';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import moment from 'moment';

const customValueProvider = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => DatetimePickerComponent),
	multi: true,
};
const customValidateProvider = {
	provide: NG_VALIDATORS,
	useExisting: forwardRef(() => DatetimePickerComponent),
	multi: true,
};

@Component({
	selector: 'date-time-picker',
	templateUrl: './date-time-picker.component.html',
	styleUrls: ['./date-time-picker.component.scss'],
	providers: [customValueProvider, customValidateProvider],
	standalone: false,
})
export class DatetimePickerComponent implements ControlValueAccessor, OnChanges, Validator {
	dataForm: FormGroup;
	@Input() startTabIndex = 0;
	@Input() label: string = 'Date and Time';
	@Input() isValid: boolean = true;
	@Input() isDisabled: boolean = false;
	@Input() errorMessage: string = '';
	@Input() showError: boolean = false;
	@Output() dateTimeExited = new EventEmitter<void>();

	writtenValue: Date | null | undefined;
	private isSynchronisingControls = false;
	private propagateValidatorChange: () => void = () => {};

	constructor(fb: FormBuilder, private formHelperService: ReactiveFormHelperService) {
		this.dataForm = fb.group({
			date: [{ value: '', disabled: this.isDisabled }, [this.isValidValidator()]],
			time: [{ value: '', disabled: this.isDisabled }, [this.isValidValidator()]],
		});

		this.dataForm.valueChanges.subscribe(() => this.onFormChanged());
	}

	// When the invalid input changes refresh the validity of the date and time controls and check for disabled
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.isDisabled) {
			if (this.isDisabled) {
				this.dataForm.get('date')?.disable();
				this.dataForm.get('time')?.disable();
			} else {
				this.dataForm.get('date')?.enable();
				this.dataForm.get('time')?.enable();
			}
		}
		if (changes.isValid) {
			this.date.updateValueAndValidity({ emitEvent: false });
			this.time.updateValueAndValidity({ emitEvent: false });
			this.propagateValidatorChange();
		}
	}

	/**
	 * Can be called when save is clicked on the parent form to mark child controls as touched and update validaiton.
	 * This appears to be necessary to resolve an issue in which the red box outline wasn't appearing if save is clicked before touching the controls.
	 */
	markAsTouchedAndUpdateValueAndValidity() {
		this.date.markAsTouched();
		this.time.markAsTouched();
		this.date.updateValueAndValidity({ emitEvent: false });
		this.time.updateValueAndValidity({ emitEvent: false });
		this.propagateValidatorChange();
	}

	markAsUntouched() {
		this.date.markAsUntouched();
		this.time.markAsUntouched();
	}

	handleExit() {
		this.markAsTouched();
		this.dateTimeExited.emit();
	}

	get date(): AbstractControl {
		return this.dataForm.controls['date'];
	}
	get time(): AbstractControl {
		return this.dataForm.controls['time'];
	}

	// required to propogate the changes to the consuming form
	onFormChanged() {
		if (this.isSynchronisingControls) {
			return;
		}

		this.propagateChange(this.getCombinedValue());
		this.propagateValidatorChange();
	}

	propagateChange: any = () => {};
	onTouched: any = () => {
		console.log('touched');
	};

	writeValue(date: Date | null | undefined): void {
		this.writtenValue = date;
		this.setControlsFromWrittenValue();
	}

	private setControlsFromWrittenValue() {
		const dateTime = this.writtenValue == null ? null : moment(this.writtenValue);
		const validDateTime = dateTime?.isValid() ? dateTime : null;

		this.isSynchronisingControls = true;
		this.date.setValue(validDateTime?.format('YYYY-MM-DD') ?? null, { emitEvent: false });
		this.time.setValue(validDateTime?.format('HH:mm') ?? null, { emitEvent: false });
		this.isSynchronisingControls = false;
		this.date.markAsPristine();
		this.time.markAsPristine();
	}

	registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	validate(control: AbstractControl): { invalidDateTime: true } | null {
		return this.hasIncompleteOrInvalidValue() ? { invalidDateTime: true } : null;
	}

	registerOnValidatorChange(fn: () => void): void {
		this.propagateValidatorChange = fn;
	}

	isValidValidator(): ValidatorFn {
		return (group: FormGroup): { [key: string]: any } => {
			return !this.isValid ? { invalid: true } : null;
		};
	}

	// Marks the control as pristine if the value has not changed from the original value.
	// This method should be called from the parent control 'cancel' method to prevent the 'confirmation dialog' from appearing
	markAsPristineIfUnchanged(control: AbstractControl, originalValue: Date) {
		const originalEventDateAndTime = formatAsString(moment(originalValue), 'YYYY-MM-DDTHH:mm:ss');
		var currentEventDateAndTime = formatAsString(control.value, 'YYYY-MM-DDTHH:mm:ss');

		var dateInvalid = this.date.value === null || this.date.value === 'Invalid date';
		var timeInvalid = this.time.value === null || this.time.value === 'Invalid date';
		var bothInvalid = dateInvalid && timeInvalid;

		// If both are null, we consider them unchanged, 'undefined' is the same as 'null' in this context
		// both invalid is being checked here because when only one part of the date/time is entered, the control value is null
		// and this is causing the cancel confirmation dialog to not appear when only one part of the date/time is changed.
		var bothAreNull = originalValue == null && control.value == null && bothInvalid;

		if (currentEventDateAndTime == originalEventDateAndTime || bothAreNull) {
			control.markAsPristine();
		}
	}

	markAsTouched() {
		this.onTouched();
	}

	private getCombinedValue(): Date | null {
		const date = this.parseDate(this.date.value);
		const time = this.parseTime(this.time.value);

		if (!date || !time) {
			return null;
		}

		return date.set({ hour: time.hour(), minute: time.minute(), second: 0, millisecond: 0 }).toDate();
	}

	private hasIncompleteOrInvalidValue(): boolean {
		const hasDate = this.hasControlValue(this.date.value);
		const hasTime = this.hasControlValue(this.time.value);

		if (!hasDate && !hasTime) {
			return false;
		}

		return !this.parseDate(this.date.value) || !this.parseTime(this.time.value);
	}

	private hasControlValue(value: any): boolean {
		return value !== null && value !== undefined && value !== '' && value !== 'Invalid date';
	}

	private parseDate(value: any): moment.Moment | null {
		if (!this.hasControlValue(value)) {
			return null;
		}

		const parsed = typeof value === 'string' ? moment(value, 'YYYY-MM-DD', true) : moment(value);
		return parsed.isValid() ? parsed : null;
	}

	private parseTime(value: any): moment.Moment | null {
		if (!this.hasControlValue(value)) {
			return null;
		}

		const parsed = typeof value === 'string' ? moment(value, 'HH:mm', true) : moment(value);
		return parsed.isValid() ? parsed : null;
	}
}
